import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";

/* hs-eslint ignored failing-rules */

/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { GREAT_WHITE, CALYPSO, BATTLESHIP, OLAF, OZ } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedPercentage from 'I18n/components/FormattedPercentage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { ANNUAL, MONTHLY } from 'self-service-api/constants/Terms';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { crmSuiteProductLevelSelector } from '../../selectors/ProductsSelectors';
import { commonAvailableTermsSelector } from '../../selectors/OfferSelectors';
const TermToggleContainer = styled.div.withConfig({
  displayName: "DetailedTermSelector__TermToggleContainer",
  componentId: "b6vso7-0"
})(["background-color:", ";border:1px solid ", ";width:100%;border-radius:3000px;position:relative;padding:6px 10px;display:flex;justify-content:space-between;cursor:pointer;"], GREAT_WHITE, BATTLESHIP);
const StyledTermOption = styled.div.withConfig({
  displayName: "DetailedTermSelector__StyledTermOption",
  componentId: "b6vso7-1"
})(["width:calc(50% - 10px);display:flex;flex-direction:column;justify-content:center;align-items:center;line-height:initial;z-index:2;text-align:center;"]);
const StyledPaymentLabel = styled.div.withConfig({
  displayName: "DetailedTermSelector__StyledPaymentLabel",
  componentId: "b6vso7-2"
})(["font-size:11px;font-weight:600;color:", ";"], ({
  selected
}) => selected ? CALYPSO : 'inherit');
const StyledCommitmentLabel = styled.div.withConfig({
  displayName: "DetailedTermSelector__StyledCommitmentLabel",
  componentId: "b6vso7-3"
})(["font-size:11px;"]);
const SelectedTerm = styled.div.withConfig({
  displayName: "DetailedTermSelector__SelectedTerm",
  componentId: "b6vso7-4"
})(["position:absolute;left:", ";height:calc(100% + 6px);width:calc(50% + 3px);background:", ";border-radius:30000px;top:-3px;border:1px solid ", ";transition:all 0.2s ease-in-out;"], ({
  activeTerm
}) => activeTerm === MONTHLY ? '-3px' : 'calc(50% - 3px)', OLAF, CALYPSO);

const getDiscountPercentageToShow = (discount1, discount2) => {
  const relativeDiscount = Math.abs(100 - discount1 - (100 - discount2)) / (100 - Math.min(discount1, discount2)) * 100; // We're slowly moving away from showing annual discounts to avoid displaying
  // arbitrary numbers in cases like this, where we're doing the calculation ourselves, such as for
  // regional promotions. Currently, we only show it when the relative discount is 10%.

  if (relativeDiscount !== 10) return null;
  return relativeDiscount;
};

const DetailedTermSelector = ({
  className,
  skuIds,
  onChange,
  trackingSource,
  screen,
  activeTerm,
  isStarterSuiteGWP
}) => {
  const suiteProductLevel = useSelector(state => crmSuiteProductLevelSelector(state, {
    skuIds
  }));
  const availableTerms = useSelector(state => commonAvailableTermsSelector(state, {
    skuIds
  })); // More context on why we're not showing the annual discount percent for CRM suite
  // https://hubspot.slack.com/archives/C824F0ZB5/p1618237316080300

  const annualDiscountPercent = suiteProductLevel ? null : availableTerms[0].discount === 0 ? availableTerms[1].discount : // For retail, both monthly and annual terms have a non-zero discount, so we can't show the
  // absolute value of the discount directly. The last part of the ternary calculates the relative
  // discount percentage for annual terms on the terms selector when both monthly and annual
  // discounts are non-zero.
  getDiscountPercentageToShow(availableTerms[0].discount, availableTerms[1] ? availableTerms[1].discount : 0);
  const discountMessage = `${Math.round(annualDiscountPercent)}PercentDiscount`;
  useEffect(() => {
    getUsageTracker().track('termSelectorInteraction', {
      action: 'view',
      discountShown: discountMessage,
      term: activeTerm,
      trackingSource,
      screen
    });
  }, []);

  const onToggleChange = () => {
    const newQueryParamTerm = activeTerm === MONTHLY ? ANNUAL : MONTHLY;
    onChange(newQueryParamTerm);
    getUsageTracker().track('termSelectorInteraction', {
      action: 'click',
      discountShown: discountMessage,
      term: newQueryParamTerm,
      trackingSource,
      screen
    });
  };

  return /*#__PURE__*/_jsx("div", {
    className: className,
    children: /*#__PURE__*/_jsxs(TermToggleContainer, {
      onClick: onToggleChange,
      "data-selenium-id": `term-selector-${trackingSource}`,
      children: [/*#__PURE__*/_jsxs(StyledTermOption, {
        children: [/*#__PURE__*/_jsx(StyledPaymentLabel, {
          selected: activeTerm === MONTHLY,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "pricingPage.detailedTermSelector.payMonthly"
          })
        }), /*#__PURE__*/_jsx(StyledCommitmentLabel, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: availableTerms[0].commitment === ANNUAL ? 'pricingPage.detailedTermSelector.commitAnnually' : 'pricingPage.detailedTermSelector.commitMonthly'
          })
        })]
      }), /*#__PURE__*/_jsx("div", {
        style: {
          width: 20
        }
      }), /*#__PURE__*/_jsxs(StyledTermOption, {
        children: [/*#__PURE__*/_jsxs(StyledPaymentLabel, {
          selected: activeTerm === ANNUAL,
          children: [/*#__PURE__*/_jsx("span", {
            style: {
              display: 'inline-block'
            },
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: availableTerms[1].payment === ANNUAL ? 'pricingPage.detailedTermSelector.payUpfront' : 'pricingPage.detailedTermSelector.payMonthly'
            })
          }), (Boolean(annualDiscountPercent) || isStarterSuiteGWP) && /*#__PURE__*/_jsx("span", {
            className: "m-left-1",
            style: {
              textTransform: 'uppercase',
              color: OZ,
              whiteSpace: 'nowrap'
            },
            children: /*#__PURE__*/_jsx(FormattedReactMessage, {
              message: "pricingPage.detailedTermSelector.saveDiscount",
              options: {
                discount: /*#__PURE__*/_jsx(FormattedPercentage, {
                  value: isStarterSuiteGWP ? 40 : Math.round(annualDiscountPercent)
                })
              }
            })
          })]
        }), /*#__PURE__*/_jsx(StyledCommitmentLabel, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "pricingPage.detailedTermSelector.commitAnnually"
          })
        })]
      }), /*#__PURE__*/_jsx(SelectedTerm, {
        activeTerm: activeTerm
      })]
    })
  });
};

export default DetailedTermSelector;